// Phase 81vv — Upcoming Tournaments horizontal scroll
import { CalendarBlank, MapPin, Calendar } from "@phosphor-icons/react";

export default function UpcomingTournaments({ tournaments = [], onSeeAll, onSelect }) {
  return (
    <section className="pt-7" data-testid="home-upcoming-section">
      <div className="px-5 flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-full bg-[#FF8A00]/15 border border-[#FF8A00]/30 flex items-center justify-center">
            <Calendar size={14} weight="bold" className="text-[#FF8A00]" />
          </span>
          <h2 className="text-[16px] sm:text-[17px] font-black tracking-wider text-white uppercase">
            Giải đấu sắp diễn ra
          </h2>
        </div>
        <button
          type="button"
          onClick={onSeeAll}
          className="text-[12px] font-semibold text-[#FF8A00] hover:opacity-80"
          data-testid="home-upcoming-see-all"
        >
          Xem tất cả ›
        </button>
      </div>

      <div className="flex gap-3 overflow-x-auto px-5 pb-2 snap-x snap-mandatory no-scrollbar">
        {tournaments.length === 0 ? (
          <div className="rounded-[24px] border border-dashed border-white/10 p-6 text-center text-[13px] text-[#94A3B8] w-full">
            Chưa có giải đấu sắp diễn ra.
          </div>
        ) : (
          tournaments.map((t) => (
            <UpcomingCard key={t.id} t={t} onClick={() => onSelect?.(t)} />
          ))
        )}
      </div>
    </section>
  );
}

function UpcomingCard({ t, onClick }) {
  const pct = Math.min(100, Math.round((t.currentReg / Math.max(t.maxReg, 1)) * 100));
  const closing = t.status === "closing" || pct >= 80;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 w-[300px] sm:w-[340px] snap-start rounded-[24px] border ${closing ? "border-[#FF8A00]/35" : "border-white/8"} bg-gradient-to-br from-[#111827]/85 to-[#0B1220]/55 backdrop-blur-xl text-left hover:border-[#FF8A00]/40 transition overflow-hidden`}
      data-testid={`home-upcoming-${t.id}`}
    >
      {/* Phase 90.C.home-upcoming-media — Banner 16:9 (khớp aspect
          upload của admin ở Step 1 wizard). `aspect-video` = 16:9 chuẩn
          → banner hiện đầy đủ không bị crop title/prize info. */}
      {t.banner && (
        <div className="w-full aspect-video relative overflow-hidden">
          <img
            src={t.banner}
            alt={t.title || "Banner giải"}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B1220]/85 via-transparent to-transparent pointer-events-none" />
        </div>
      )}

      <div className="p-4">
        <div className="flex items-start gap-3">
          <div
            className={`w-14 h-14 rounded-2xl shrink-0 border border-white/8 overflow-hidden ${
              t.logo ? "bg-white/5" : "bg-gradient-to-br from-[#0B1220] to-[#1F2937] flex items-center justify-center text-[10px] font-black text-white/80 tracking-tight"
            }`}
          >
            {t.logo ? (
              <img
                src={t.logo}
                alt={t.title || "Logo giải"}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              (t.shortLogo || t.title || "??").slice(0, 3).toUpperCase()
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-bold text-white leading-snug line-clamp-2">
              {t.title}
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-[#94A3B8] mt-1.5 font-medium">
              <CalendarBlank size={11} weight="bold" />
              <span>{t.date}</span>
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-[#94A3B8] mt-0.5 font-medium">
              <MapPin size={11} weight="bold" />
              <span className="truncate">{t.location}</span>
            </div>
          </div>
          <CircularProgress pct={pct} value={t.currentReg} max={t.maxReg} closing={closing} />
        </div>
        <div className="mt-3 flex items-center justify-between">
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${closing ? "bg-[#FF8A00]/15 text-[#FF8A00]" : "bg-[#A8FF00]/10 text-[#A8FF00]"}`}
          >
            {closing ? "Sắp đóng đăng ký" : "Đăng ký mở"}
          </span>
        </div>
      </div>
    </button>
  );
}

function CircularProgress({ pct, value, max, closing }) {
  const r = 22;
  const c = 2 * Math.PI * r;
  const dash = (pct / 100) * c;
  const stroke = closing ? "#FF8A00" : "#A8FF00";
  return (
    <div className="relative w-[58px] h-[58px] shrink-0">
      <svg viewBox="0 0 56 56" className="w-full h-full -rotate-90">
        <circle cx="28" cy="28" r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="4" fill="none" />
        <circle
          cx="28" cy="28" r={r}
          stroke={stroke}
          strokeWidth="4"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`}
          style={{ transition: "stroke-dasharray 0.6s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <div className="text-[11px] font-black text-white tabular-nums leading-none">
          {value}
        </div>
        <div className="text-[8px] text-[#94A3B8] tabular-nums leading-none mt-0.5">/{max}</div>
      </div>
    </div>
  );
}
